import { required, maxLength, minValue, integer, helpers } from '@vuelidate/validators'

const holidayJp = require('@holiday-jp/holiday_jp')

export const entryFormRules = {
  client: {
    required: requiredWithMessage('クライアント名'),
    maxLength: maxLengthWithMessage(200)
  },
  product: {
    required: requiredWithMessage('案件名・作品番号'),
    maxLength: maxLengthWithMessage(200)
  },
  director: { maxLength: maxLengthWithMessage(200) },
  producer: { maxLength: maxLengthWithMessage(200) },
  camera: { maxLength: maxLengthWithMessage(200) },
  budget: {
    minValue: minValueWithMessage(0),
    integer: integerWithMessage()
  },
  note: { maxLength: maxLengthWithMessage(2000) },
  assignedSalesId: {
    required: requiredWithMessage('弊社希望営業'),
    integer: integerWithMessage()
  },
  creatorId: {
    required: requiredWithMessage('エントリー作成者'),
    integer: integerWithMessage()
  },
  updaterId: {
    required: requiredWithMessage('エントリー更新者'),
    integer: integerWithMessage()
  },
  entryKinds: [{
    kindId: {
      integer: integerWithMessage()
    }
  }],
  types: {
    $each: helpers.forEach({
      durationId: {
        required: requiredWithMessage('尺'),
        integer: integerWithMessage()
      },
      count: {
        required: requiredWithMessage('タイプ数'),
        integer: integerWithMessage(),
        minValue: minValueWithMessage(1)
      }
    })
  },
  entryUsers: {
    $each: helpers.forEach({
      userId: {
        required: requiredWithMessage('担当製作者'),
        integer: integerWithMessage()
      }
    })
  },
  entryCameraman: {
    customCameramanValidation: {
      $validator: validateEntryCameraman,
      $message: 'カメラマンの値が不正です'
    }
  },
  categoryNotes: {
    $each: helpers.forEach({
      categoryId: {
        required: requiredWithMessage('カテゴリ'),
        integer: integerWithMessage()
      },
      note: { maxLength: maxLengthWithMessage(2000) }
    })
  },
  reservationRequests: {
    required: requiredWithMessage('1つ以上の日時'),
    $each: helpers.forEach({
      categoryId: {
        required: requiredWithMessage('カテゴリ'),
        integer: integerWithMessage()
      },
      reservationRequestId: {
        integer: integerWithMessage()
      },
      startDate: {
        required: requiredWithMessage('開始日'),
        minDateValidation: {
          $validator: (date, dateObject) => validateStartDate(date, dateObject, 2),
          $message: '2営業日以内は直接お問い合わせください'
        },
        maxDateValidation: {
          $validator: (date) => validateMaxMonth(date, 3),
          $message: '3ヶ月以内を指定してください'
        }
      },
      endDate: {
        required: requiredWithMessage('終了日'),
        startEndValidation: {
          $validator: (date, dateObject) => endDate(date, dateObject),
          $message: '開始日よりあとを選択してください'
        },
        maxDateValidation: {
          $validator: (date) => validateMaxMonth(date, 3),
          $message: '3ヶ月以内を指定してください'
        }
      },
      requiredDays: {
        required: requiredWithMessage('作業日数'),
        integer: integerWithMessage(),
        minValue: minValueWithMessage(1),
        maxDaysValidation: {
          $validator: (days, dateObject) => maxDays(days, dateObject),
          $message: '期間内の日数を指定してください'
        }
      }
    })
  },
  designations: {
    $each: helpers.forEach({
      categoryId: {
        required: requiredWithMessage('カテゴリ'),
        integer: integerWithMessage()
      },
      designationTypeId: {
        customDesignationTypeValidation: {
          $validator: (designationTypeId, designation) => validateDesignationType(designationTypeId, designation),
          $message: '指名理由を指定してください'
        },
        integer: integerWithMessage()
      },
      unlistedDesignation: {
        maxLength: maxLengthWithMessage(200),
        customUnlistedDesignationValidation: {
          $validator: validateUnlistedDesignation,
          $message: 'スタッフを指定してください'
        }
      },
      priority: {
        integer: integerWithMessage()
      },
      workerId: {
        integer: integerWithMessage(),
        customDesignationWorkerValidation: {
          $validator: validateDesignationWorker,
          $message: 'スタッフを指定してください'
        }
      }
    })
  }
}

export const minDate = (maxCount) => {
  const day = new Date(new Date().setHours(0, 0, 0, 0))
  let count = 0
  while (count < maxCount + 1) {
    if (day.getDay() === 0 || day.getDay() === 6 || holidayJp.between(day, day).length === 1) {
      day.setDate(day.getDate() + 1)
      continue
    }
    count++
    day.setDate(day.getDate() + 1)
  }
  day.setDate(day.getDate() - 1)
  return day
}

export const maxDate = (maxCountMonth) => {
  const day = new Date(new Date().setHours(0, 0, 0, 0))
  day.setMonth(day.getMonth() + maxCountMonth)
  return day
}

function maxDays (days, dateObject) {
  return days <= ((new Date(dateObject.endDate) - new Date(dateObject.startDate)) / 86400000) + 1
}

function endDate (date, dateObject) {
  return new Date(date) >= new Date(dateObject.startDate)
}

function validateStartDate (date, dateObject, maxCount) {
  if (dateObject.reservationRequestId) return true
  return validateMinDate(date, maxCount)
}

function validateMinDate (date, maxCount) {
  return minDate(maxCount) <= date
}

function validateMaxMonth (date, maxCountMonth) {
  return maxDate(maxCountMonth) >= date
}

function validateDesignationType (designationTypeId, designation) {
  if (designationTypeId === undefined && designation.workerId) return false
  return designationTypeId || designation.workerId === null
}

// カメラマンの選択または自由入力のどちらかが指定されていること
function validateEntryCameraman (cameraman) {
  let result = true
  if (cameraman !== null) {
    if (cameraman.cameramanId) {
      if (cameraman.unlistedCameraman || isNaN(cameraman.cameramanId)) {
        result = false
      }
    } else if (cameraman.unlistedCameraman) {
      if (cameraman.cameramanId || cameraman.unlistedCameraman.length >= 200) {
        result = false
      }
    }
  }
  return result
}

// 弊社スタッフの選択または外部スタッフのどちらかが指定されていること
function validateDesignationWorker (workerId, designationObject) {
  let result = true
  if (designationObject.unlistedDesignation || !workerId) {
    if (workerId !== null) {
      result = false
    }
  }
  return result
}

// 弊社スタッフの選択または外部スタッフのどちらかが指定されていること
function validateUnlistedDesignation (unlistedDesignation, designationObject) {
  let result = true
  if (designationObject.workerId || unlistedDesignation.length <= 0) {
    result = false
  }
  return result
}

function maxLengthWithMessage (count) {
  return helpers.withMessage(`${count}文字以下で入力してください`, maxLength(count))
}

function minValueWithMessage (count) {
  return helpers.withMessage(`${count}以上を入力してください`, minValue(count))
}

function integerWithMessage () {
  return helpers.withMessage('整数で入力してください', integer)
}

function requiredWithMessage (value) {
  return helpers.withMessage(`${value}を入力してください`, required)
}
